import type { ReactNode } from 'react';
import { MISSING } from '../inventoryView';
import { useModalDialog } from '@/pages/fulfillment/components/useModalDialog';
import type { AuditTally, InventoryScreenRow } from '../inventoryView';

/** One shelf line the floor counted differently from the book. */
export interface AuditDifference {
  row: InventoryScreenRow;
  countedQty: number;
  diff: number;
}

/** A count below the reservation: how many units the holding orders are owed and will not get. */
export interface AuditShortfall {
  sku: string;
  locationCode: string;
  reserved: number;
  countedQty: number;
  shortfall: number;
}

export interface ConfirmAuditModalProps {
  differences: AuditDifference[];
  shortfalls: AuditShortfall[];
  tally: AuditTally;
  busy: boolean;
  signedOff: boolean;
  onSignOffChange: (signedOff: boolean) => void;
  onConfirm: () => void;
  onClose: () => void;
  notifications?: ReactNode;
}

/** Only a differing line is recorded, as an ADJUST correction: an inbound would dirty the logistics history [7]. */
export const ConfirmAuditModal = ({
  differences,
  shortfalls,
  tally,
  busy,
  signedOff,
  onSignOffChange,
  onConfirm,
  onClose,
  notifications,
}: ConfirmAuditModalProps) => {
  const dialog = useModalDialog(onClose);

  return (
    <dialog
      className="inv-modal audit-result-modal"
      ref={dialog}
      aria-label="Confirm audit differences"
      onClick={(event) => event.target === event.currentTarget && onClose()}
    >
      <header>
        Confirm Audit Differences
        <button type="button" className="x" aria-label="Close audit confirmation" onClick={onClose}>
          ✕
        </button>
      </header>
      <div className="body">
        <table className="tbl">
          <thead>
            <tr>
              <th>SKU</th>
              <th>Product</th>
              <th>Location</th>
              <th>System</th>
              <th>Counted</th>
              <th>Diff</th>
            </tr>
          </thead>
          <tbody>
            {differences.map(({ row, countedQty, diff }) => (
              <tr key={`${row.sku}@${row.locationCode}`}>
                <td className="num">{row.sku}</td>
                <td>{row.nameKor ?? row.name ?? MISSING}</td>
                <td>
                  <span className="loc-pill">{row.locationCode || 'Unassigned'}</span>
                </td>
                <td className="num">{row.onHand}</td>
                <td className="num">{countedQty}</td>
                <td className={`num ${diff < 0 ? 'diff-neg' : 'diff-pos'}`}>{diff > 0 ? `+${diff}` : diff}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {/* The number is the point: "check those orders" without a figure is a sentence nobody can act on. */}
        {shortfalls.length > 0 && (
          <div className="note warn">
            <div>
              These counts leave holding orders short — check those orders with the order team before confirming.
            </div>
            <ul>
              {shortfalls.map((entry) => (
                <li key={`${entry.sku}@${entry.locationCode}`}>
                  {`${entry.sku} · ${entry.locationCode || 'Unassigned'} — counted ${entry.countedQty} against ${entry.reserved} reserved, ${entry.shortfall} short`}
                </li>
              ))}
            </ul>
            <label htmlFor="audit-signoff">
              <input
                id="audit-signoff"
                type="checkbox"
                checked={signedOff}
                onChange={(event) => onSignOffChange(event.target.checked)}
              />{' '}
              I have confirmed with the order team
            </label>
          </div>
        )}
        <div className="note">
          {`Adjustments: ${tally.adjustments} (−${tally.decreases} / +${tally.increases})`} — each shelf is recorded on
          its own: a shelf that is refused leaves the others confirmed, and recording again sends only the shelves still
          open.
        </div>
      </div>
      <div className="audit-result-notifications">{notifications}</div>
      <div className="foot">
        <button type="button" className="btn btn-gray btn-sm" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-blue btn-sm"
          disabled={busy || (shortfalls.length > 0 && !signedOff)}
          onClick={onConfirm}
        >
          {`Record ${tally.adjustments} ADJUST ${tally.adjustments === 1 ? 'event' : 'events'}`}
        </button>
      </div>
    </dialog>
  );
};
